import axios from 'axios';

/**
 * Cliente HTTP exclusivo do modo kiosk (TV/showroom).
 * Não usa o token do usuário nem o interceptor que redireciona para /auth/login:
 * a TV se autentica pelo header X-Kiosk-Token.
 */

const TOKEN_KEY = 'kiosk-device-token';

export const getKioskToken = (): string | null => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch (error) {
    return null;
  }
};

export const setKioskToken = (token: string) => {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch (error) {
    // navegador sem storage: a TV vai pedir novo código ao recarregar
  }
};

export const clearKioskToken = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch (error) {
    // ignora
  }
};

const kioskApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL_BACKEND,
  timeout: 20000,
});

kioskApi.interceptors.request.use((config) => {
  const token = getKioskToken();
  if (token && config.headers) {
    config.headers['X-Kiosk-Token'] = token;
  }
  return config;
});

export type KioskPairStatus = 'PENDING' | 'ACTIVE' | 'REVOKED' | 'EXPIRED' | 'INVALID';

export type KioskPlant = {
  id: string;
  name: string;
  status: number | null;
  type_plant: string | null;
  capacity: string | null;
  pac: number | null;
  energyToday: number | null;
  energyMonth: number | null;
  alarmsCount: number | null;
  mark: { name: string; link_imagem: string | null } | null;
  percentage_day: number;
  percentage_15days: number;
  percentage_30days: number;
  percentage_12mouth: number;
};

export type KioskPlantsPage = {
  total: number;
  page: number;
  perPage: number;
  lastPage: number;
  data: KioskPlant[];
};

export type KioskSummary = {
  normalNum: number;
  offlineNum: number;
  attention: number;
  info: number;
  unknown: number;
  alertCount: number;
  total: number;
  companyName: string;
  deviceName: string | null;
  // muda sempre que alguma usina da empresa é alterada/importada/removida
  version: string;
};

export const postKioskPair = async (): Promise<{ code: string; deviceToken: string; expiresAt: string }> => {
  const response = await kioskApi.post('kiosk/pair');
  return response.data;
};

export const getKioskPairStatus = async (): Promise<{ status: KioskPairStatus; name?: string }> => {
  const response = await kioskApi.get('kiosk/pair/status');
  return response.data;
};

export const getKioskPlants = async (page: number): Promise<KioskPlantsPage> => {
  const response = await kioskApi.get(`kiosk/plants?page=${page}`);
  return response.data;
};

export const getKioskSummary = async (): Promise<KioskSummary> => {
  const response = await kioskApi.get('kiosk/summary');
  return response.data;
};

export const getKioskLogo = async (): Promise<{ logo: string | null }> => {
  const response = await kioskApi.get('kiosk/logo');
  return response.data;
};

export const isKioskUnauthorized = (error: any) => error?.response?.status === 401;
